/**
 * Marquee — continuously-scrolling horizontal banner.
 *
 * Useful for ticker strips that list active theorems, recent
 * decision-receipt IDs, or status feeds where the content density
 * exceeds the available width. The component duplicates the supplied
 * children (so the scroll loop stays seamless) and pauses on hover
 * to make the strip readable without forcing the user to chase the
 * content.
 *
 * Honors `prefers-reduced-motion`: under reduced motion the marquee
 * does not animate and the duplicated content is hidden so the strip
 * reads as a static one-line label.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { useReducedMotion } from "motion/react";
import { cn } from "../../styling/index.js";

export interface MarqueeProps {
  /** Marquee items. Each item is rendered as a child of the strip. */
  readonly children: React.ReactNode;
  /** Scroll direction. Defaults to `left`. */
  readonly direction?: "left" | "right";
  /** Scroll speed in seconds per full revolution. Defaults to 24. */
  readonly speedSeconds?: number;
  /** Whether to pause when the user hovers the strip. Defaults to true. */
  readonly pauseOnHover?: boolean;
  /** Gap between items, as a CSS length. Defaults to `2rem`. */
  readonly gap?: string;
  /** Accessible label for the marquee region. */
  readonly ariaLabel?: string;
  /** Additional className applied to the outer wrapper. */
  readonly className?: string;
}

const KEYFRAMES_LEFT = `
  @keyframes csk-marquee-left {
    from { transform: translateX(0); }
    to { transform: translateX(-50%); }
  }
`;

const KEYFRAMES_RIGHT = `
  @keyframes csk-marquee-right {
    from { transform: translateX(-50%); }
    to { transform: translateX(0); }
  }
`;

/** Renders a continuously-scrolling marquee strip. */
export const Marquee = React.memo(function Marquee({
  children,
  direction = "left",
  speedSeconds = 24,
  pauseOnHover = true,
  gap = "2rem",
  ariaLabel = "Marquee",
  className,
}: MarqueeProps): React.JSX.Element {
  const prefersReducedMotion = useReducedMotion() ?? false;
  const animationName = direction === "left" ? "csk-marquee-left" : "csk-marquee-right";

  return (
    <div
      role="region"
      aria-label={ariaLabel}
      data-csk-marquee-direction={direction}
      className={cn("relative overflow-hidden", className)}
    >
      <style>{`
        ${KEYFRAMES_LEFT}
        ${KEYFRAMES_RIGHT}
        .csk-marquee-track {
          animation: ${animationName} ${speedSeconds}s linear infinite;
        }
        .csk-marquee-pause:hover .csk-marquee-track { animation-play-state: paused; }
        @media (prefers-reduced-motion: reduce) {
          .csk-marquee-track { animation: none; }
        }
      `}</style>
      <div className={pauseOnHover ? "csk-marquee-pause" : undefined}>
        <div
          className="csk-marquee-track"
          data-csk-marquee-track
          style={{
            display: "flex",
            gap,
            whiteSpace: "nowrap",
            width: "max-content",
          }}
        >
          <div style={{ display: "flex", gap, alignItems: "center" }}>{children}</div>
          {!prefersReducedMotion && (
            <div
              aria-hidden="true"
              data-csk-marquee-duplicate
              style={{ display: "flex", gap, alignItems: "center" }}
            >
              {children}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});
